import { Link } from "react-router";
import { AlertCircle, CheckCircle2, ClipboardList, Clock } from "lucide-react";
import { SkeletonCard } from "@/components/skeleton";
import { useTaskList, type TaskSummary } from "@/hooks/use-tasks";
import { formatMonthDay } from "@/lib/i18n";
import {
  buildCrmTaskActionGroups,
  buildCrmTaskContactChannelActionGroup,
  type CrmTaskAction,
  type CrmTaskActionGroup,
} from "@ease/crm/task-action-groups";
import {
  buildCrmTaskProfileSections,
  summarizeCrmTaskProfileSections,
  type CrmTaskProfileSectionTone,
} from "@ease/crm/task-profile-sections";
import { shouldOpenCrmActionHrefInNewTab } from "@ease/crm/safe-action-hrefs";

const MAX_ITEMS_PER_GROUP = 4;

type TaskRow = TaskSummary;
type OverviewSignalTone = "default" | "success" | "warning" | "danger" | "info";

const OVERVIEW_SIGNAL_CLASS: Record<OverviewSignalTone, string> = {
  default: "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)]",
  success: "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]",
  warning: "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  danger: "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
  info: "border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 text-[var(--color-primary)]",
};

const OVERVIEW_ACTION_CLASS: Record<CrmTaskAction["tone"], string> = {
  neutral: "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]",
  primary: "border-[var(--color-primary)]/25 bg-[var(--color-primary)]/5 text-[var(--color-primary)]",
  success: "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]",
  warning: "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  danger: "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
};

function formatTaskDate(value: string | null) {
  if (!value) {
    return "No due date";
  }

  return formatMonthDay(value);
}

function formatTaskLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_.-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatContextValue(value: unknown): string | null {
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return null;
}

function readRecordValue(
  records: Array<Record<string, unknown> | null | undefined>,
  keys: string[],
): string | null {
  for (const record of records) {
    if (!record) continue;
    for (const key of keys) {
      const value = formatContextValue(record[key]);
      if (value) return value;
    }
  }
  return null;
}

function readTaskOverviewContext(task: TaskRow): string | null {
  if (task.operatorReadModel?.contextSummary?.trim()) {
    return task.operatorReadModel.contextSummary.trim();
  }

  const records = [task.contextSnapshot ?? {}, task.metadata ?? {}];
  const personName = readRecordValue(records, ["contactName", "personName", "customerName", "clientName", "learnerName"]);
  const companyName = readRecordValue(records, ["companyName", "accountName"]);
  const signal = formatTaskLabel(task.triggerType)
    ?? formatTaskLabel(readRecordValue(records, ["sourceEventType", "triggerType", "signalFamily"]))
    ?? formatTaskLabel(task.sourceRuleId);
  const score = readRecordValue(records, ["leadScore", "score", "diagnosticScore", "healthScore"]);
  const intent = formatTaskLabel(readRecordValue(records, ["intent", "intentClass", "consultationIntent", "scoreBand"]));
  const parts = [
    personName ? `For ${companyName ? `${personName} (${companyName})` : personName}` : null,
    signal ? `Signal ${signal}` : null,
    score || intent ? [score ? `Score ${score}` : null, intent].filter(Boolean).join(" · ") : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}

function readTaskOverviewGuidance(task: TaskRow): string | null {
  return task.operatorReadModel?.guidance?.trim() || null;
}

function formatTaskSlaMinutes(value: number | null): string | null {
  if (typeof value !== "number" || Number.isNaN(value)) return null;
  const absoluteMinutes = Math.abs(value);
  if (absoluteMinutes < 60) return `${absoluteMinutes}m`;
  const hours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

function getTaskOverviewSlaSignal(task: TaskRow): { label: string; tone: OverviewSignalTone } | null {
  const sla = task.operatorReadModel?.sla;
  if (!sla || sla.status === "unscheduled") return null;
  const delta = formatTaskSlaMinutes(sla.minutesUntilDue);
  const label = delta
    ? `${sla.label} (${sla.minutesUntilDue != null && sla.minutesUntilDue < 0 ? `${delta} late` : `${delta} left`})`
    : sla.label;
  return {
    label,
    tone: sla.severity === "critical" ? "danger" : sla.severity === "warning" ? "warning" : "info",
  };
}

function getTaskOverviewContextSignal(task: TaskRow): { label: string; tone: OverviewSignalTone; missing: string[] } | null {
  const completeness = task.operatorReadModel?.contextCompleteness;
  if (!completeness) return null;
  return {
    label: completeness.isUsable ? "Operator-ready context" : "Needs operator context",
    tone: completeness.isUsable ? "success" : "warning",
    missing: completeness.missing,
  };
}

function getTaskOverviewCompletionLabel(task: TaskRow): string | null {
  const schema = task.operatorReadModel?.completionSchema;
  if (!schema) return null;
  const outcomeCount = schema.outcomes.length;
  const fieldCount = schema.fields.length;
  return `${formatTaskLabel(schema.category) ?? schema.category} completion: ${outcomeCount} ${outcomeCount === 1 ? "outcome" : "outcomes"}, ${fieldCount} ${fieldCount === 1 ? "field" : "fields"}`;
}

function getTaskOverviewOperatorBrief(task: TaskRow): Array<{ label: string; value: string }> {
  return (task.operatorReadModel?.operatorBrief ?? [])
    .map((row) => ({ label: row.label, value: row.value.trim() }))
    .filter((row) => row.value)
    .slice(0, 3);
}

function taskOverviewEvidenceToneClass(tone: CrmTaskProfileSectionTone): string {
  if (tone === "neutral") return OVERVIEW_SIGNAL_CLASS.default;
  return OVERVIEW_SIGNAL_CLASS[tone];
}

function getTaskOverviewQuickActions(groups: readonly CrmTaskActionGroup[]): CrmTaskAction[] {
  return groups
    .flatMap((group) => group.actions)
    .filter((action) => !action.disabled)
    .slice(0, 3);
}

function Section({
  title,
  emptyLabel,
  icon: Icon,
  items,
}: {
  title: string;
  emptyLabel: string;
  icon: typeof AlertCircle;
  items: TaskRow[];
}) {
  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4 text-[var(--color-primary)]" />
        <h3 className="text-sm font-semibold text-[var(--color-foreground)]">{title}</h3>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">{emptyLabel}</p>
      ) : (
        <div className="space-y-2">
          {items.map((task) => (
            <TaskOverviewCard key={task.id} task={task} />
          ))}
        </div>
      )}
    </section>
  );
}

function TaskOverviewCard({ task }: { task: TaskRow }) {
  const context = readTaskOverviewContext(task);
  const guidance = readTaskOverviewGuidance(task);
  const contextSignal = getTaskOverviewContextSignal(task);
  const slaSignal = getTaskOverviewSlaSignal(task);
  const completionLabel = getTaskOverviewCompletionLabel(task);
  const operatorBrief = getTaskOverviewOperatorBrief(task);
  const priorityLabel = task.operatorReadModel?.labels.priority ?? formatTaskLabel(task.priority);
  const sourceLabel = task.operatorReadModel?.labels.source ?? formatTaskLabel(task.source);
  const taskEvidenceRecords = [task.metadata, task.contextSnapshot];
  const taskProfileSummary = summarizeCrmTaskProfileSections(
    buildCrmTaskProfileSections(taskEvidenceRecords, { maxSections: 3, maxItemsPerSection: 5 }),
  );
  const taskActionGroups = [
    buildCrmTaskContactChannelActionGroup(taskEvidenceRecords, { maxActions: 4 }),
    ...buildCrmTaskActionGroups(taskEvidenceRecords, { maxGroups: 3, maxActionsPerGroup: 4 }),
  ].filter((group): group is CrmTaskActionGroup => Boolean(group));
  const taskQuickActions = getTaskOverviewQuickActions(taskActionGroups);

  return (
    <div className="stagger-item rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2">
      <p className="text-sm font-medium text-[var(--color-foreground)]">{task.title}</p>
      {(guidance || task.description || context) && (
        <p className="mt-1 line-clamp-2 text-xs text-[var(--color-muted-foreground)]">
          {guidance ?? task.description ?? context}
        </p>
      )}
      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
        {task.completedAt ? `Completed ${formatTaskDate(task.completedAt)}` : `Due ${formatTaskDate(task.dueAt)}`}
        {priorityLabel ? ` · ${priorityLabel}` : ""}
        {sourceLabel ? ` · ${sourceLabel}` : ""}
      </p>
      {(contextSignal || slaSignal || completionLabel) && (
        <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
          {contextSignal && (
            <span className={`rounded-full border px-2 py-0.5 ${OVERVIEW_SIGNAL_CLASS[contextSignal.tone]}`}>
              {contextSignal.label}
            </span>
          )}
          {slaSignal && (
            <span className={`rounded-full border px-2 py-0.5 ${OVERVIEW_SIGNAL_CLASS[slaSignal.tone]}`}>
              SLA: {slaSignal.label}
            </span>
          )}
          {completionLabel && (
            <span className={`rounded-full border px-2 py-0.5 ${OVERVIEW_SIGNAL_CLASS.default}`}>
              {completionLabel}
            </span>
          )}
        </div>
      )}
      {(taskProfileSummary || taskQuickActions.length > 0) && (
        <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]" aria-label="Task overview reusable evidence">
          {taskProfileSummary && (
            <span className={`rounded-full border px-2 py-0.5 ${taskOverviewEvidenceToneClass(taskProfileSummary.tone)}`}>
              Evidence: {taskProfileSummary.primaryLabel} · {taskProfileSummary.primaryValue}
            </span>
          )}
          {taskQuickActions.length > 0 && (
            <span className="flex flex-wrap gap-1" aria-label="Task overview quick actions">
              {taskQuickActions.map((action) => (
                <TaskOverviewActionLink key={`${action.key}:${action.href}`} action={action} />
              ))}
            </span>
          )}
        </div>
      )}
      {operatorBrief.length > 0 && (
        <dl className="mt-2 grid grid-cols-[88px_1fr] gap-x-2 gap-y-0.5 text-[10px]">
          {operatorBrief.map((row) => (
            <div key={`${row.label}:${row.value}`} className="contents">
              <dt className="text-[var(--color-muted-foreground)]">{row.label}</dt>
              <dd className="truncate text-[var(--color-foreground)]">{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {contextSignal && contextSignal.missing.length > 0 && (
        <p className="mt-2 text-[10px] text-[var(--color-warning)]">
          Missing {contextSignal.missing.map(formatTaskLabel).filter(Boolean).join(", ")}
        </p>
      )}
      <div className="mt-2 flex flex-wrap gap-2 text-xs">
        {task.personId && <Link to={`/crm/contacts/${task.personId}`} className="text-[var(--color-primary)] hover:underline">Contact</Link>}
        {task.dealId && <Link to={`/crm/deals/${task.dealId}`} className="text-[var(--color-primary)] hover:underline">Deal</Link>}
        <Link
          to={`/crm/tasks/${encodeURIComponent(task.id)}`}
          className="text-[var(--color-muted-foreground)] hover:text-[var(--color-primary)] hover:underline"
        >
          Open details
        </Link>
      </div>
    </div>
  );
}

function TaskOverviewActionLink({ action }: { action: CrmTaskAction }) {
  const className = [
    "inline-flex items-center rounded-full border px-2 py-0.5 font-medium transition-colors",
    OVERVIEW_ACTION_CLASS[action.tone] ?? OVERVIEW_ACTION_CLASS.neutral,
  ].join(" ");

  if (action.href.startsWith("/") && !action.href.startsWith("//")) {
    return (
      <Link to={action.href} className={className} title={action.description ?? undefined}>
        {action.label}
      </Link>
    );
  }

  const opensNewTab = shouldOpenCrmActionHrefInNewTab(action.href);
  return (
    <a
      href={action.href}
      className={className}
      title={action.description ?? undefined}
      target={opensNewTab ? "_blank" : undefined}
      rel={opensNewTab ? "noreferrer" : undefined}
    >
      {action.label}
    </a>
  );
}

export function TaskOverview() {
  const pendingTasks = useTaskList({ limit: 12, status: "pending" });
  const completedTasks = useTaskList({ limit: MAX_ITEMS_PER_GROUP, status: "completed" });
  const now = Date.now();
  const isLoading = pendingTasks.isLoading || completedTasks.isLoading;
  const error = pendingTasks.error ?? completedTasks.error;

  const overdue = pendingTasks.tasks
    .filter((task) => task.status !== "completed" && task.dueAt && new Date(task.dueAt).getTime() < now)
    .sort((a, b) => new Date(a.dueAt ?? 0).getTime() - new Date(b.dueAt ?? 0).getTime())
    .slice(0, MAX_ITEMS_PER_GROUP);

  const upcoming = pendingTasks.tasks
    .filter((task) => task.status !== "completed" && (!task.dueAt || new Date(task.dueAt).getTime() >= now))
    .sort((a, b) => {
      if (!a.dueAt && !b.dueAt) return 0;
      if (!a.dueAt) return 1;
      if (!b.dueAt) return -1;
      return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
    })
    .slice(0, MAX_ITEMS_PER_GROUP);

  const recentlyCompleted = completedTasks.tasks
    .filter((task) => task.completedAt)
    .sort((a, b) => new Date(b.completedAt ?? 0).getTime() - new Date(a.completedAt ?? 0).getTime())
    .slice(0, MAX_ITEMS_PER_GROUP);

  return (
    <div className="space-y-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-[var(--color-primary)]" />
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Task Overview</h2>
          </div>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Your overdue, upcoming, and recently completed tasks at a glance.
          </p>
        </div>

        <Link
          to="/crm/tasks"
          className="inline-flex items-center rounded-md border border-[var(--color-border)] px-3 py-2 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
        >
          Open task workspace
        </Link>
      </div>

      {error ? (
        <div className="rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {error}
        </div>
      ) : isLoading ? (
        <div className="grid gap-3 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <SkeletonCard key={index} className="h-36" />
          ))}
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-3">
          <Section
            title="Overdue"
            emptyLabel="Nothing overdue."
            icon={AlertCircle}
            items={overdue}
          />
          <Section
            title="Upcoming"
            emptyLabel="No upcoming tasks."
            icon={Clock}
            items={upcoming}
          />
          <Section
            title="Recently completed"
            emptyLabel="No completed tasks yet."
            icon={CheckCircle2}
            items={recentlyCompleted}
          />
        </div>
      )}
    </div>
  );
}
